<template>
  <AppLayout
    page-title="On Site Hub"
    page-subtitle="Field tools for your assigned company and location"
  >
    <div class="space-y-6 pt-4">
      <div>
        <h1 class="text-3xl font-bold text-white">On Site Tools</h1>
        <p class="text-slate-400 mt-1">
          Worker-facing field tools only. Company administration and job management are not included.
        </p>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <button
          v-for="tool in tools"
          :key="tool.id"
          class="tool-card text-left"
          :class="{ 'tool-card-active': activeTool === tool.id }"
          @click="selectTool(tool.id)"
        >
          <img :src="tool.icon" :alt="tool.label" class="w-10 h-10 mb-4" />
          <h2 class="font-semibold text-white">{{ tool.label }}</h2>
          <p class="text-sm text-slate-400 mt-1">{{ tool.description }}</p>
        </button>
      </div>

      <div class="panel">
        <div class="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p class="text-xs font-semibold uppercase tracking-wider text-emerald-400">Selected tool</p>
            <h2 class="text-2xl font-bold text-white mt-1">{{ currentTool.label }}</h2>
            <p class="text-slate-400 mt-2">{{ currentTool.detail }}</p>
          </div>
          <span class="px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-sm">
            {{ isContractor ? 'Contractor access' : 'Employee access' }}
          </span>
        </div>

        <div class="mt-6 border-t border-slate-700/60 pt-6">
          <p class="text-slate-300">
            Records for this tool are tied to your company assignment. You can enter and review field
            information without access to payroll setup, CRM, hiring, job posting, or other company controls.
          </p>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppLayout from '@/components/layout/AppLayout.vue'

const route = useRoute()
const router = useRouter()

const isContractor = computed(() =>
  (localStorage.getItem('user_type') || '') === 'contractor' || route.path === '/onsite' || route.path.startsWith('/dispatch') || route.path.startsWith('/tank')
)
const isSupervisor = computed(() => {
  const level = localStorage.getItem('crew_level') || ''
  return ['supervisor_1', 'supervisor_2', 'supervisor_3'].includes(level)
})
function fieldPath(slug: string) {
  if (isContractor.value) {
    const paths: Record<string, string> = {
      'field-tickets': '/field-tickets',
      dispatch: '/dispatch',
      'tank-sheets': '/tank-sheets',
      tanks: '/tanks',
      'tank-watch': '/tank-watch',
      'well-head-sheets': '/well-head-sheets',
      'field-tools': '/onsite',
    }
    return paths[slug] || '/onsite'
  }
  const paths: Record<string, string> = {
    'field-tickets': '/employee/field-tickets',
    dispatch: '/employee/dispatch',
    'daily-report': '/employee/daily-report',
    'tank-sheets': '/employee/tank-sheets',
    tanks: '/employee/tanks',
    'tank-watch': '/employee/tank-watch',
    'well-head-sheets': '/employee/well-head-sheets',
    'field-tools': '/employee/onsite',
  }
  return paths[slug] || '/employee/onsite'
}

const allTools = [
  {
    id: 'field-tickets',
    label: 'Field Tickets',
    icon: '/src/assets/images/icons/todo-list.png',
    description: 'Log labor, equipment, materials',
    detail: 'Create and submit wellsite tickets for company-man approval and billing.',
  },
  {
    id: 'dispatch',
    label: 'My Dispatch',
    icon: '/src/assets/images/icons/map location.png',
    description: 'Confirm callouts and pad work',
    detail: 'See where you’re assigned, confirm callouts, and mark shifts started or complete.',
  },
  {
    id: 'daily-report',
    label: 'Daily Report',
    icon: '/src/assets/images/icons/checklist and pen.png',
    description: 'Supervisor ops report',
    detail: 'Assemble tanks, tank sheet, and well head into one report for the company man.',
    supervisorOnly: true,
  },
  {
    id: 'tank-sheets',
    label: 'Tank Sheets',
    icon: '/src/assets/images/icons/todo-list.png',
    description: 'Record field tank measurements',
    detail: 'Enter and review tank sheet readings for your assigned location.',
  },
  {
    id: 'tanks',
    label: 'Tanks',
    icon: '/src/assets/images/icons/oilbuddy_logo.png',
    description: 'Set up the pad battery with the company man',
    detail: 'Add frac and upright tanks the way this pad is laid out. Hands use Tank Watch and Tank Sheets after setup.',
    supervisorOnly: true,
  },
  {
    id: 'tank-watch',
    label: 'Tank Watch',
    icon: '/src/assets/images/icons/map location.png',
    description: 'Flag tanks and notify the driver',
    detail: 'Watch live frac tanks, set haul flags, and send bring-in or disposal calls.',
  },
  {
    id: 'well-head-sheets',
    label: 'Well Head Sheets',
    icon: '/src/assets/images/icons/checklist and pen.png',
    description: 'Complete well-head inspections',
    detail: 'Complete well-head checks and review your recent inspection sheets.',
  },
  {
    id: 'field-tools',
    label: 'Field Tools',
    icon: '/src/assets/images/icons/map location.png',
    description: 'Assigned on-site work',
    detail: 'Access the field records and operational tools assigned by your company.',
  },
]

const tools = computed(() =>
  allTools.filter((tool) => !tool.supervisorOnly || isSupervisor.value)
)

const activeTool = computed(() => {
  const pathTool: Record<string, string> = {
    '/employee/field-tickets': 'field-tickets',
    '/field-tickets': 'field-tickets',
    '/employee/dispatch': 'dispatch',
    '/dispatch': 'dispatch',
    '/employee/daily-report': 'daily-report',
    '/employee/tank-sheets': 'tank-sheets',
    '/tank-sheets': 'tank-sheets',
    '/employee/tanks': 'tanks',
    '/tanks': 'tanks',
    '/employee/tank-watch': 'tank-watch',
    '/tank-watch': 'tank-watch',
    '/employee/well-head-sheets': 'well-head-sheets',
    '/well-head-sheets': 'well-head-sheets',
  }
  if (pathTool[route.path]) return pathTool[route.path]
  const requested = String(route.query.tool || 'field-tools')
  return tools.value.some((tool) => tool.id === requested) ? requested : 'field-tools'
})

const currentTool = computed(() => tools.value.find((tool) => tool.id === activeTool.value) || tools.value[tools.value.length - 1])

function selectTool(id: string) {
  router.push(fieldPath(id))
}
</script>

<style scoped>
.tool-card {
  @apply bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5 transition-all hover:border-emerald-500/40 hover:bg-slate-800/80;
}
.tool-card-active {
  @apply border-emerald-500/60 bg-emerald-500/10;
}
.panel {
  @apply bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6;
}
</style>
